import { useState } from "react";
import {
  useGetAgencyStatus,
  getAgencyAgreement,
  acceptAgencyAgreement,
} from "../../../../lib/api-client-react/src/agency/agency";
import {
  type AgencyStatus,
  type AgencyDocument,
  protectedMenuHeadline,
} from "../../../../lib/agency-contracts";
import { SignaturePad } from "./ShopTermsGate";
const ackLabels = {
  agencyCommission:
    "I understand that Ayrshire Hub retains an agency commission from the customer-facing item selling price.",
  protectedEntitlement:
    "I understand that this commission is not deducted again from my protected menu price entitlement.",
  pricingMandate:
    "I authorise the variable pricing mandate, including the existing shop, category and item configurations and the agreed variation procedure.",
  authorisedSignatory:
    "I am authorised to bind this business and confirm the legal identity and my signing capacity.",
};
export default function AgencyAgreementPanel({
  storeId,
  audience = "shop",
}: {
  storeId?: number;
  audience?: "shop" | "customer" | "driver";
}) {
  const query = useGetAgencyStatus<AgencyStatus>(
    { audience, storeId },
    {
      query: {
        queryKey: ["agency-status", audience, storeId],
        retry: false,
        enabled: audience !== "shop" || !!storeId,
      },
    },
  );
  const [doc, setDoc] = useState<AgencyDocument | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [name, setName] = useState(""),
    [signature, setSignature] = useState<string | null>(null),
    [acks, setAcks] = useState<Record<string, boolean>>({}),
    [identity, setIdentity] = useState<Record<string, string>>({}),
    [done, setDone] = useState(false);
  // An unavailable status never blocks shop orders or customer checkout.
  if (audience === "customer" || !query.data?.agreement || query.isError) return null;
  const status = query.data;
  const agreement=query.data.agreement;
  async function open() {
    setBusy(true);
    setError("");
    try {
      setDoc(
        (await getAgencyAgreement(agreement.id, {
          storeId,
        })) as unknown as AgencyDocument,
      );
    } catch {
      setError("The agreement could not be loaded. Please retry.");
    } finally {
      setBusy(false);
    }
  }
  async function accept() {
    if (!doc) return;
    setBusy(true);
    setError("");
    try {
      await acceptAgencyAgreement(doc.id, {
        storeId,
        contentHash: doc.content_hash,
        scheduleHash: doc.scheduleHash,
        signedName: name,
        signatureDataUrl: signature,
        legalIdentity: identity,
        acknowledgements: acks,
      });
      setDone(true);
      await query.refetch();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not save acceptance. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  }
  const accepted =
    status.status === "Active" || status.status === "Accepted" || done;
  const shop = audience === "shop";
  const requiresSignature = shop || audience === "driver";
  return (
    <section className="my-4 rounded-2xl border bg-white p-5">
      <h2 className="text-base font-bold">
        {shop ? protectedMenuHeadline : "Updated terms available"}
      </h2>
      <p className="mt-2 text-sm text-slate-600">{agreement.summary}</p>
      <p className="mt-2 text-xs text-slate-500">
        {agreement.version} · {status.status}. Existing order processing
        continues.
      </p>
      <button
        disabled={busy}
        className="mt-3 rounded-lg border px-4 py-2 text-sm font-semibold"
        onClick={() => void open()}
      >
        {busy
          ? "Loading…"
          : accepted
            ? "Read agreement and current schedule"
            : "Review and sign"}
      </button>
      {error && !doc && (
        <p role="alert" className="mt-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      {doc && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Review agreement"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-2"
        >
          <div className="flex max-h-[94dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white">
            <header className="flex items-center justify-between gap-3 border-b p-4">
              <h3 className="font-bold">Review {doc.audience} agreement</h3>
              <button
                disabled={busy}
                onClick={() => setDoc(null)}
                className="rounded-lg border px-3 py-2"
              >
                Close
              </button>
            </header>
            <div className="overflow-y-auto overscroll-contain p-5">
              <p className="mb-3 text-xs text-slate-500">
                Exact version: {doc.version} · Previous agreements remain
                retained.
              </p><p className="mb-4 text-sm font-semibold">Approved earliest effective date: {doc.state.effectiveAt?new Date(doc.state.effectiveAt).toLocaleString('en-GB'):'Not approved'}. Your acceptance cannot apply before you sign.</p>
              {shop && doc.commercialSchedule && (
                <div className="mb-5 rounded-xl bg-emerald-50 p-4 text-sm">
                  <p className="font-bold">
                    Worked example at this shop’s current default uplift
                  </p>
                  <p className="mt-1">
                    Protected price £
                    {(
                      doc.commercialSchedule.example.protectedPence / 100
                    ).toFixed(2)}{" "}
                    · Customer item price £
                    {(
                      doc.commercialSchedule.example.customerPence / 100
                    ).toFixed(2)}{" "}
                    · Commission £
                    {(
                      doc.commercialSchedule.example.commissionPence / 100
                    ).toFixed(2)}
                    .
                  </p>
                  <p className="mt-2">
                    Configured default:{" "}
                    {doc.commercialSchedule.example.configuredPercent}%.
                    Category and item overrides may differ. The £1.50 customer
                    platform fee remains separate.
                  </p>
                  <details className="mt-2">
                    <summary>
                      Read the exact current commercial schedule
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap break-all text-xs">
                      {JSON.stringify(doc.commercialSchedule, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
              <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7">
                {doc.content}
              </pre>
              <p className="mt-3 break-all text-xs text-slate-500">
                Content SHA-256: {doc.content_hash}
              </p>
              {!accepted && (
                <form
                  className="mt-6 space-y-4 border-t pt-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void accept();
                  }}
                >
                  {shop && (
                    <>
                      <p className="text-sm font-semibold">
                        Business identity and authority
                      </p>
                      {[
                        ["legalName", "Legal business name"],
                        ["tradingName", "Trading name"],
                        ["address", "Registered / business address"],
                        ["capacity", "Your signing capacity"],
                        ["companyNumber", "Company number (if applicable)"],
                        ["vatNumber", "VAT number (if registered)"],
                      ].map(([k, label]) => (
                        <label className="block text-sm" key={k}>
                          {label}
                          <input
                            required={[
                              "legalName",
                              "tradingName",
                              "address",
                              "capacity",
                            ].includes(k)}
                            value={identity[k] ?? ""}
                            onChange={(e) =>
                              setIdentity({ ...identity, [k]: e.target.value })
                            }
                            maxLength={500}
                            className="mt-1 w-full rounded-lg border p-3"
                          />
                        </label>
                      ))}
                      <p className="text-xs text-slate-500">
                        These are your declared details. They are not
                        automatically verified VAT-registration evidence.
                      </p>
                    </>
                  )}
                  {Object.entries(
                    shop
                      ? ackLabels
                      : {
                          termsAccepted:
                            "I have read and explicitly accept this exact prospective version.",
                        },
                  ).map(([k, label]) => (
                    <label
                      key={k}
                      className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        required
                        checked={!!acks[k]}
                        onChange={(e) =>
                          setAcks({ ...acks, [k]: e.target.checked })
                        }
                        className="mt-1 h-4 w-4 shrink-0"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                  <label className="block text-sm">
                    Full name
                    <input
                      required
                      minLength={3}
                      maxLength={200}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-lg border p-3"
                      autoComplete="name"
                    />
                  </label>
                  {requiresSignature && <SignaturePad onChange={setSignature} />}
                  <p className="text-xs text-slate-500">
                    Acceptance is prospective from the approved effective date
                    or your acceptance time, whichever is later. No past
                    transaction is rewritten.
                  </p>
                  {error && (
                    <p role="alert" className="text-sm text-rose-700">
                      {error}
                    </p>
                  )}
                  <button
                    disabled={busy || (requiresSignature && !signature)}
                    className="w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white disabled:opacity-50"
                  >
                    {busy ? "Saving evidence…" : "Accept this version"}
                  </button>
                </form>
              )}
              {accepted && (
                <div className="mt-4 rounded-lg bg-emerald-50 p-4 text-sm">
                  Accepted evidence is retained. Your protected entitlement,
                  existing pricing engine, payouts and order processing remain
                  unchanged.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {query.data.acceptance?.id && (
        <button
          className="mt-3 ml-2 rounded-lg border px-4 py-2 text-sm font-semibold"
          onClick={async () => {
            setError("");
            try {
              const token = localStorage.getItem("ayrshire_token");
              const r = await fetch(
                `/api/agency/acceptances/${query.data!.acceptance!.id}/copy`,
                { headers: { Authorization: `Bearer ${token ?? ""}` } },
              );
              if (!r.ok) throw Error();
              const url = URL.createObjectURL(await r.blob());
              const a = document.createElement("a");
              a.href = url;
              a.download = `agreement-acceptance-${query.data!.acceptance!.id}.pdf`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch {
              setError(
                "Could not download your retained agreement. Please retry.",
              );
            }
          }}
        >
          Download signed copy
        </button>
      )}
    </section>
  );
}
